import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  adminService,
  type CatalogImportDraftItem,
} from "@/services/adminService";
import {
  Alert,
  Button,
  Input,
  Select,
  Textarea,
  EmptyState,
} from "@/components/ui";
import { createActionsColumn, type DataTableColumn } from "@/components/table";
import {
  ImportSection,
  type ImportRowActions,
  type ImportSectionLabels,
} from "@/components/common/ImportSection";

import { newClientKey } from "@/lib/clientKey";
import { toastApiError } from "@/lib/errorMessages";
import { formatMoneyInput, parseMoneyInputToCents } from "@/lib/money";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import type { Activity } from "@/types";

interface AdminActivityCatalogImportSectionProps {
  activity: Activity;
}

type ImportLabel = {
  id: string;
  name: string;
};

type DraftRow = CatalogImportDraftItem & {
  clientKey: string;
  priceEuro: string;
  labelId: string | null;
};

function normalizeLabelName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function buildImportLabels(
  existingNames: string[],
  extractedNames: string[],
): ImportLabel[] {
  const labels: ImportLabel[] = [];
  const seen = new Set<string>();

  for (const raw of [...existingNames, ...extractedNames]) {
    const name = raw.trim();
    if (!name) continue;
    const key = normalizeLabelName(name);
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push({ id: newClientKey(), name });
  }

  return labels;
}

function resolveLabelId(
  labels: ImportLabel[],
  categoryLabel: string | null | undefined,
): string | null {
  const raw = categoryLabel?.trim();
  if (!raw) return null;
  const key = normalizeLabelName(raw);
  return (
    labels.find((label) => normalizeLabelName(label.name) === key)?.id ?? null
  );
}

export function AdminActivityCatalogImportSection({
  activity,
}: AdminActivityCatalogImportSectionProps) {
  const { t, i18n } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();

  const existingCategoryNames = useMemo(() => {
    const names = (activity.service_categories ?? [])
      .map((category) =>
        resolveCatalogTranslation(
          category.name_translations,
          i18n.language,
        ).trim(),
      )
      .filter(Boolean);
    return [...new Set(names)];
  }, [i18n.language, activity.service_categories]);

  const [importLabels, setImportLabels] = useState<ImportLabel[]>([]);
  const [newLabelDraft, setNewLabelDraft] = useState("");
  const [wasTranslated, setWasTranslated] = useState(false);
  const [draftLanguage, setDraftLanguage] = useState<string | null>(null);
  const [detectedSourceLanguage, setDetectedSourceLanguage] = useState<
    string | null
  >(null);

  const confirmMutation = useMutation({
    mutationFn: ({
      items,
      labels,
    }: {
      items: CatalogImportDraftItem[];
      labels: string[];
    }) => adminService.confirmCatalogImport(activity.id, items, labels),
    onSuccess: async (created) => {
      toast.success(
        t("detail.adminCatalogImportToastSaved", { count: created.length }),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["serviceItems", activity.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["menuItems", activity.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["activity", activity.id],
        }),
        queryClient.invalidateQueries({ queryKey: ["activities"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "activities"] }),
      ]);
    },
    onError: (err) => {
      toastApiError(err, "updateActivity");
    },
  });

  const renameLabel = (labelId: string, nextName: string) => {
    setImportLabels((labels) =>
      labels.map((label) =>
        label.id === labelId ? { ...label, name: nextName } : label,
      ),
    );
  };

  const removeLabel = (labelId: string) => {
    setImportLabels((labels) => labels.filter((label) => label.id !== labelId));
  };

  const addLabel = () => {
    const name = newLabelDraft.trim();
    if (!name) {
      toast.error(t("detail.adminCatalogImportLabelNameRequired"));
      return;
    }
    const key = normalizeLabelName(name);
    if (importLabels.some((label) => normalizeLabelName(label.name) === key)) {
      toast.error(t("detail.adminCatalogImportLabelDuplicate"));
      return;
    }
    setImportLabels((labels) => [...labels, { id: newClientKey(), name }]);
    setNewLabelDraft("");
  };

  const labels: ImportSectionLabels = {
    title: t("detail.adminCatalogImportTitle"),
    barHint: t("detail.adminCatalogImportBarHint"),
    adminBadge: t("detail.adminOnlyBadge"),
    open: t("detail.adminCatalogImportOpen"),
    modalDescription: t("detail.adminCatalogImportHint"),
    cancel: t("detail.adminCatalogImportCancel"),
    close: tCommon("close"),
    extract: t("detail.adminCatalogImportExtract"),
    save: t("detail.adminCatalogImportSave"),
    dropHint: t("detail.adminCatalogImportDropHint"),
    fileLabel: t("detail.adminCatalogImportFileLabel"),
    previewCount: (count) =>
      t("detail.adminCatalogImportPreviewCount", { count }),
    chooseAnother: t("detail.adminCatalogImportChooseAnother"),
    emptyPreview: t("detail.adminCatalogImportEmptyPreview"),
    pickFile: t("detail.adminCatalogImportPickFile"),
  };

  const labelSelectOptions = useMemo(
    () => [
      {
        value: "",
        label: t("detail.adminCatalogImportCategoryNone"),
      },
      ...importLabels.map((label) => ({
        value: label.id,
        label: label.name.trim() || t("detail.adminCatalogImportLabelUntitled"),
      })),
    ],
    [importLabels, t],
  );

  type CatalogSortColumn = "name" | "price";

  const buildColumns = ({
    updateRow,
    removeRow,
    isBusy,
  }: ImportRowActions<DraftRow>): DataTableColumn<
    DraftRow,
    CatalogSortColumn
  >[] => [
    {
      id: "name",
      header: t("detail.adminCatalogImportName"),
      sortKey: "name",
      getSortValue: (row) => row.name,
      defaultWidth: 180,
      cell: (row) => (
        <Input
          value={row.name}
          disabled={isBusy}
          className="h-9"
          onChange={(event) =>
            updateRow(row.clientKey, {
              name: event.target.value,
            })
          }
        />
      ),
    },
    {
      id: "category",
      header: t("detail.adminCatalogImportCategory"),
      defaultWidth: 160,
      cell: (row) => (
        <Select
          options={labelSelectOptions}
          value={row.labelId ?? ""}
          disabled={isBusy}
          onChange={(event) =>
            updateRow(row.clientKey, {
              labelId: event.target.value || null,
            })
          }
        />
      ),
    },
    {
      id: "description",
      header: t("detail.adminCatalogImportDescription"),
      defaultWidth: 220,
      cell: (row) => (
        <Textarea
          value={row.description}
          rows={2}
          disabled={isBusy}
          className="min-h-[2.5rem] py-2"
          onChange={(event) =>
            updateRow(row.clientKey, {
              description: event.target.value,
            })
          }
        />
      ),
    },
    {
      id: "price",
      header: t("detail.adminCatalogImportPrice"),
      sortKey: "price",
      sortKind: "number",
      getSortValue: (row) => parseMoneyInputToCents(row.priceEuro) ?? null,
      defaultWidth: 110,
      cell: (row) => (
        <Input
          inputMode="decimal"
          value={row.priceEuro}
          disabled={isBusy}
          className="h-9 tabular-nums"
          onChange={(event) =>
            updateRow(row.clientKey, {
              priceEuro: event.target.value,
            })
          }
        />
      ),
    },
    createActionsColumn("", {
      defaultWidth: 56,
      minWidth: 56,
      cell: (row) => (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-error-600"
          disabled={isBusy}
          aria-label={t("detail.adminCatalogImportRemoveRow")}
          onClick={() => removeRow(row.clientKey)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    }),
  ];

  const validateBeforeSave = (draftRows: DraftRow[]): boolean => {
    const trimmedLabels = importLabels.map((label) => ({
      ...label,
      name: label.name.trim(),
    }));
    for (const label of trimmedLabels) {
      if (!label.name) {
        toast.error(t("detail.adminCatalogImportLabelNameRequired"));
        return false;
      }
    }
    const seen = new Set<string>();
    for (const label of trimmedLabels) {
      const key = normalizeLabelName(label.name);
      if (seen.has(key)) {
        toast.error(t("detail.adminCatalogImportLabelDuplicate"));
        return false;
      }
      seen.add(key);
    }

    const labelNameById = new Map(
      trimmedLabels.map((label) => [label.id, label.name]),
    );

    for (const row of draftRows) {
      const name = row.name.trim();
      if (!name) {
        toast.error(t("detail.adminCatalogImportNameRequired"));
        return false;
      }
      const priceCents = parseMoneyInputToCents(row.priceEuro);
      if (priceCents === null) {
        toast.error(t("detail.adminCatalogImportPriceInvalid"));
        return false;
      }
      if (row.labelId && !labelNameById.has(row.labelId)) {
        toast.error(t("detail.adminCatalogImportLabelMissing"));
        return false;
      }
    }
    return true;
  };

  const previewTopContent = ({ isBusy }: { isBusy: boolean }) => (
    <>
      {wasTranslated ? (
        <Alert
          variant="warning"
          className="rounded-lg px-3 py-2"
        >
          {t("detail.adminCatalogImportTranslatedBanner", {
            language: draftLanguage ?? "—",
            source: detectedSourceLanguage ?? "—",
          })}
        </Alert>
      ) : null}

      <section className="border-surface-200 bg-surface-50/80 space-y-3 rounded-xl border p-3">
        <div>
          <h3 className="text-surface-800 text-sm font-medium">
            {t("detail.adminCatalogImportLabelsTitle")}
          </h3>
          <p className="text-surface-500 mt-0.5 text-xs">
            {t("detail.adminCatalogImportLabelsHint")}
          </p>
        </div>

        {importLabels.length === 0 ? (
          <EmptyState
            variant="dashed"
            size="sm"
            showIcon={false}
            title={t("detail.adminCatalogImportLabelsEmpty")}
            className="bg-surface-0 rounded-lg py-4"
          />
        ) : (
          <ul className="space-y-2">
            {importLabels.map((label) => (
              <li
                key={label.id}
                className="flex items-center gap-2"
              >
                <Input
                  value={label.name}
                  disabled={isBusy}
                  className="h-9"
                  placeholder={t(
                    "detail.adminCatalogImportAddLabelPlaceholder",
                  )}
                  aria-label={t("detail.adminCatalogImportCategory")}
                  onChange={(event) =>
                    renameLabel(label.id, event.target.value)
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-error-600 shrink-0"
                  disabled={isBusy}
                  aria-label={t("detail.adminCatalogImportRemoveLabel")}
                  onClick={() => removeLabel(label.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-2">
          <Input
            value={newLabelDraft}
            disabled={isBusy}
            className="h-9"
            placeholder={t("detail.adminCatalogImportAddLabelPlaceholder")}
            aria-label={t("detail.adminCatalogImportAddLabelPlaceholder")}
            onChange={(event) => setNewLabelDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addLabel();
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={isBusy}
            onClick={addLabel}
          >
            <Plus className="mr-1 h-4 w-4" />
            {tCommon("add")}
          </Button>
        </div>
      </section>
    </>
  );

  return (
    <ImportSection<DraftRow, CatalogSortColumn>
      entityKey={`catalog-${activity.id}`}
      preferencesKey="activity-catalog-import"
      defaultSortColumn="name"
      labels={labels}
      buildColumns={buildColumns}
      previewTopContent={previewTopContent}
      validateBeforeSave={validateBeforeSave}
      onExtract={async (file) => {
        const preview = await adminService.extractCatalogFromPdf(
          activity.id,
          file,
        );
        const extractedNames = preview.items
          .map((item) => item.category_label?.trim() ?? "")
          .filter(Boolean);
        const builtLabels = buildImportLabels(
          existingCategoryNames,
          extractedNames,
        );
        setImportLabels(builtLabels);
        setWasTranslated(preview.was_translated_to_activity_language);
        setDetectedSourceLanguage(preview.detected_source_language);
        setDraftLanguage(preview.activity_language);
        return preview.items.map((item) => ({
          ...item,
          clientKey: newClientKey(),
          priceEuro: formatMoneyInput(item.price_cents),
          labelId: resolveLabelId(builtLabels, item.category_label),
        }));
      }}
      onExtractSuccess={(rows) => {
        toast.success(
          t("detail.adminCatalogImportToastExtracted", {
            count: rows.length,
          }),
        );
      }}
      onExtractError={(err) => toastApiError(err, "updateActivity")}
      onConfirm={async (draftRows) => {
        const trimmedLabels = importLabels.map((label) => ({
          ...label,
          name: label.name.trim(),
        }));
        const labelNameById = new Map(
          trimmedLabels.map((label) => [label.id, label.name]),
        );
        const items: CatalogImportDraftItem[] = draftRows.map((row) => ({
          name: row.name.trim(),
          description: row.description.trim(),
          price_cents: parseMoneyInputToCents(row.priceEuro)!,
          category_label: row.labelId
            ? (labelNameById.get(row.labelId) ?? null)
            : null,
        }));
        await confirmMutation.mutateAsync({
          items,
          labels: trimmedLabels.map((label) => label.name),
        });
      }}
      onConfirmError={(err) => toastApiError(err, "updateActivity")}
      onResetImportState={() => {
        setImportLabels([]);
        setNewLabelDraft("");
        setWasTranslated(false);
        setDetectedSourceLanguage(null);
        setDraftLanguage(null);
      }}
    />
  );
}
